<script setup>
/**
 * The bench the suggester is tuned on — Settings' Suggestions tab.
 *
 * Every parameter the model has, with what it does written next to it, and a
 * mark out of a hundred that re-runs as you move them. The mark is taken on
 * the most recent quarter of your ledger, which the model is not trained on —
 * the question being asked is "will it get the next one right", not "can it
 * remember the last one".
 *
 * The sweep tries one parameter across its whole range and reports every
 * result, so a choice can be made from a column of numbers rather than from a
 * feeling about which way the slider went.
 */
import { computed, ref } from 'vue'
import Icon from '../ui/Icon.vue'
import { t } from '../../stores/i18n'
import {
  modelQuality,
  resetSuggestSettings,
  setSuggestSetting,
  suggestSettings,
  trainingSet,
  wantSuggestions,
} from '../../stores/suggest'
import { FEATURE_FLAGS, PARAMETERS, evaluate } from '../../lib/suggest'

// The lab measures the model, so it needs the rows it learns from — asked for
// only once this tab is first opened, since it means reading every row.
wantSuggestions()

const sweeping = ref('')
const sweepOf = ref(null)
const sweepRows = ref([])

const quality = computed(() => modelQuality.value)
const pct = (value) => (value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`)

/** A number the slider can hold, whatever the input hands back. */
const setNumber = (key, raw) => setSuggestSetting(key, Number(raw))

/**
 * One parameter, across its range, scored. Yields to the browser between runs
 * so the progress actually shows rather than the tab locking up.
 */
async function sweep(parameter) {
  sweeping.value = parameter.key
  sweepOf.value = parameter
  sweepRows.value = []
  const steps = []
  const span = parameter.max - parameter.min
  const stride = Math.max(parameter.step, span / 8)
  for (let v = parameter.min; v <= parameter.max + 1e-9; v += stride) {
    steps.push(Math.round(v / parameter.step) * parameter.step)
  }
  for (const value of [...new Set(steps)]) {
    await new Promise((r) => setTimeout(r))
    const marks = evaluate(trainingSet.value, { ...suggestSettings.value, [parameter.key]: value })
    sweepRows.value = [
      ...sweepRows.value,
      {
        value: Math.round(value * 10000) / 10000,
        category: marks.enough ? marks.category.precision : null,
        f1: marks.enough ? marks.tags.f1 : null,
      },
    ]
  }
  sweeping.value = ''
}

/** The best row of a sweep, by tag F1 and then by category precision. */
const bestSweep = computed(() => {
  const scored = sweepRows.value.filter((r) => r.f1 !== null || r.category !== null)
  if (!scored.length) return null
  return [...scored].sort(
    (a, b) => (b.f1 ?? 0) - (a.f1 ?? 0) || (b.category ?? 0) - (a.category ?? 0),
  )[0]
})
</script>

<template>
  <div class="grid gap-4" data-suggestions>
      <p class="text-[12.5px] text-faint">{{ t('settings.suggestHint') }}</p>
      <!-- What it is being judged on, before anything it scored. -->
      <div class="rounded-xl border border-line-soft bg-raised/50 p-3.5">
        <p class="text-[12.5px] font-semibold text-muted">{{ t('lab.scoreboard') }}</p>
        <p v-if="!quality.enough" class="mt-1.5 text-[13px] text-muted">
          {{ t('lab.notEnough', { count: trainingSet.length }) }}
        </p>
        <dl v-else class="tnum mt-2 grid grid-cols-2 gap-y-1.5 text-[13px]">
          <dt class="text-muted">{{ t('lab.categoryPrecision') }}</dt>
          <dd class="text-right font-semibold">{{ pct(quality.category.precision) }}</dd>
          <dt class="text-muted">{{ t('lab.categoryCoverage') }}</dt>
          <dd class="text-right">{{ pct(quality.category.coverage) }}</dd>
          <dt class="text-muted">{{ t('lab.tagPrecision') }}</dt>
          <dd class="text-right">{{ pct(quality.tags.precision) }}</dd>
          <dt class="text-muted">{{ t('lab.tagRecall') }}</dt>
          <dd class="text-right">{{ pct(quality.tags.recall) }}</dd>
          <dt class="font-medium">{{ t('lab.f1') }}</dt>
          <dd class="text-right font-semibold">{{ pct(quality.tags.f1) }}</dd>
        </dl>
        <p v-if="quality.enough" class="mt-2 text-[12px] text-faint">
          {{ t('lab.split', { trained: quality.trained, tested: quality.tested }) }}
        </p>
      </div>

      <div v-for="parameter in PARAMETERS" :key="parameter.key">
        <div class="flex items-baseline justify-between gap-2">
          <label class="label !mb-0" :for="`lab-${parameter.key}`">{{ t(`lab.${parameter.key}`) }}</label>
          <span class="tnum text-[12.5px] font-semibold">{{ suggestSettings[parameter.key] }}</span>
        </div>
        <input
          :id="`lab-${parameter.key}`"
          type="range"
          class="mt-1.5 w-full accent-[var(--c-accent)]"
          :min="parameter.min"
          :max="parameter.max"
          :step="parameter.step"
          :value="suggestSettings[parameter.key]"
          @input="setNumber(parameter.key, $event.target.value)"
        />
        <div class="mt-1 flex items-start justify-between gap-2">
          <p class="text-[12px] text-faint">{{ t(`lab.${parameter.key}Hint`) }}</p>
          <button
            class="btn-ghost shrink-0 !px-1.5 !py-0.5 !text-[12px]"
            :disabled="!!sweeping || !quality.enough"
            @click="sweep(parameter)"
          >
            {{ sweeping === parameter.key ? t('lab.sweeping') : t('lab.sweep') }}
          </button>
        </div>
      </div>

      <div>
        <p class="label">{{ t('lab.features') }}</p>
        <div class="grid gap-1.5">
          <label
            v-for="flag in FEATURE_FLAGS"
            :key="flag.key"
            class="flex items-start gap-3 rounded-xl border border-line-soft bg-raised/50 p-3"
          >
            <input
              type="checkbox"
              class="mt-0.5 size-4 accent-[var(--c-accent)]"
              :checked="suggestSettings[flag.key]"
              @change="setSuggestSetting(flag.key, $event.target.checked)"
            />
            <span>
              <span class="block text-[13.5px] font-medium">{{ t(`lab.${flag.key}`) }}</span>
              <span class="mt-0.5 block text-[12.5px] text-muted">{{ t(`lab.${flag.key}Hint`) }}</span>
            </span>
          </label>
        </div>
      </div>

      <!-- A sweep's whole column of results, because the point is comparing. -->
      <div v-if="sweepRows.length" class="rounded-xl border border-line-soft bg-raised/50 p-3.5">
        <p class="text-[12.5px] font-semibold text-muted">
          {{ t('lab.sweepOf', { name: t(`lab.${sweepOf.key}`) }) }}
        </p>
        <table class="tnum mt-2 w-full text-left text-[12.5px]">
          <thead class="text-faint">
            <tr>
              <th class="py-1 font-medium">{{ t('lab.value') }}</th>
              <th class="py-1 text-right font-medium">{{ t('lab.categoryPrecision') }}</th>
              <th class="py-1 text-right font-medium">{{ t('lab.f1') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in sweepRows"
              :key="row.value"
              class="border-t border-line-soft"
              :class="row.value === suggestSettings[sweepOf.key] && 'text-accent'"
            >
              <td class="py-1">{{ row.value }}</td>
              <td class="py-1 text-right">{{ pct(row.category) }}</td>
              <td class="py-1 text-right" :class="bestSweep === row && 'font-semibold'">
                {{ pct(row.f1) }}
              </td>
            </tr>
          </tbody>
        </table>
        <button
          v-if="bestSweep"
          class="btn-soft mt-2 !py-1.5 !text-[12.5px]"
          @click="setNumber(sweepOf.key, bestSweep.value)"
        >
          {{ t('lab.useBest', { value: bestSweep.value }) }}
        </button>
      </div>

      <p class="text-[12px] text-faint">{{ t('lab.footnote') }}</p>
      <div>
        <button class="btn-soft" @click="resetSuggestSettings">{{ t('lab.reset') }}</button>
      </div>
  </div>
</template>
